import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  TextField,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import { WrappedFieldProps } from 'redux-form';

import { useDebounce } from 'utils/useDebounce';
import { renderAddress } from 'utils/renderAddress';
import { AddressValidationError } from 'utils/AddressValidationError';

import { validateAndMapAddress } from './validateAndMapAddress';
import { supportedCountries } from './supportedCountries';

interface Props extends WrappedFieldProps {
  label?: string;
  helperText?: string;
  setFieldError?: (errorMessage: string | undefined) => void;
  forceCountryCode?: string;
}

export const FormAddressValidationField = ({
  input,
  label,
  helperText,
  setFieldError,
  meta,
  forceCountryCode,
}: Props) => {
  const [address, setAddress] = useState(renderAddress(input.value?.toJS?.() || ''));
  const [countryCode, setCountryCode] = useState(forceCountryCode || 'US');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  const debouncedAddress = useDebounce(address, 1000);

  const { error } = meta;

  const validateAddressHandler = async (_address: string, _countryCode: string) => {
    setFieldError?.(undefined);
    setLoading(true);

    try {
      const locationObject = await validateAndMapAddress(_address, _countryCode);
      input.onChange(locationObject);
      setFieldError?.(undefined);
    } catch (err) {
      if (err instanceof AddressValidationError) {
        setFieldError?.(err.message);
      } else {
        // eslint-disable-next-line no-console
        console.error(err);
        Sentry.captureException(err, { level: 'error' });
        setFieldError?.("There's been an error trying to verify the address, please try again");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debouncedAddress && touched) {
      void validateAddressHandler(debouncedAddress, countryCode);
    }
  }, [debouncedAddress, countryCode, touched]);

  return (
    <>
      <Box display="flex">
        <TextField
          label={label}
          helperText={error || helperText}
          variant="standard"
          fullWidth
          value={address}
          onChange={(e) => {
            setTouched(true);
            setAddress(e.target.value);
          }}
          error={Boolean(error)}
          style={{
            marginRight: 16,
            flexGrow: 1,
          }}
        />
        {!forceCountryCode && (
          <FormControl fullWidth style={{ maxWidth: 150 }}>
            <InputLabel id="country-label">Country</InputLabel>
            <Select
              labelId="country-label"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value as string)}
              label="Country"
            >
              {supportedCountries.map((country) => (
                <MenuItem key={country.alpha2} value={country.alpha2}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {loading && <CircularProgress size={24} />}
    </>
  );
};
